/**
 * Email Template Registry — Types
 *
 * The registry is the single source of truth for every transactional email the
 * Platform sends. Each entry pairs metadata (id, category, data source) with a
 * pure `render(data)` that returns the exact subject + HTML a worker delivers,
 * plus production-safe `sampleData` for previews and the email test harness.
 *
 * This file holds only the shape; concrete entries live in `./index.ts`.
 */

/**
 * Stable identifiers for every registered email template. The tuple is the single
 * source of truth — the `EmailTemplateId` union is derived from it.
 */
export const EMAIL_TEMPLATE_IDS = [
  'test',
  'welcome',
  'detection-alert',
  'daily-summary',
  'payment-failed-final',
  'payment-recovered',
  'subscription-canceled',
  'reactivation-confirmed',
] as const

export type EmailTemplateId = (typeof EMAIL_TEMPLATE_IDS)[number]

/** Bounded-context grouping for an email template. */
export type EmailTemplateCategory = 'transactional' | 'alert' | 'billing'

/**
 * The domain data a template needs to render. Drives how the email test harness
 * resolves real data for a given Organization (or uses a fixed sample when
 * `'none'`).
 */
export type EmailTemplateDataSource =
  | 'none'
  | 'organization'
  | 'organization+subscription'
  | 'detection'
  | 'user'

/** The exact output a worker delivers for a rendered template. */
export type EmailRenderResult = {
  subject: string
  html: string
  text?: string
}

/**
 * A single registered template. `render` is pure: same `data` → same
 * `{ subject, html }`. `sampleData` is production-safe (no secrets, no PII) so it
 * can ship in the bundle for previews.
 */
export type EmailTemplateEntry<TData> = {
  id: EmailTemplateId
  name: string
  description: string
  category: EmailTemplateCategory
  dataSource: EmailTemplateDataSource
  render: (data: TData) => EmailRenderResult
  sampleData: TData
}

/**
 * Type-erased entry for storing heterogeneous templates side by side. Each entry
 * keeps its own data type internally; consumers that need the concrete type fold
 * it via `entry.render(entry.sampleData)`.
 */
export type AnyEmailTemplateEntry = EmailTemplateEntry<unknown>

/**
 * Register a template with its concrete data type, erasing it to
 * `AnyEmailTemplateEntry` for the registry collection. The single localized cast
 * is sound: `render`/`sampleData` are defined together over the same `TData`.
 */
export function defineEmailTemplate<TData>(
  entry: EmailTemplateEntry<TData>
): AnyEmailTemplateEntry {
  return entry as AnyEmailTemplateEntry
}
